import i18n from '@/i18n';
import {
  GOODS_STUDIO_DEFAULT_PARAMS,
  GOODS_STUDIO_MODEL_MODE_OPTIONS,
  normalizeGoodsStudioParams,
} from '@/pages/ipe/workbench/constants/aiStudioConfig';
import { DEFAULT_MODEL_TRY_ON_ETHNICITY } from '@/pages/ipe/workbench/constants/modelStudioConfig';
import { resolveImg2ImgReferenceForApi } from '@/pages/ipe/workbench/utils/img2imgPublicUrl';
import {
  buildModelEthnicitySystemModelSelectionParams,
  resolveModelEthnicityPresetPersistedUrl,
} from '@/pages/ipe/workbench/utils/systemModelEthnicityPresetCatalog';
import { buildTryOnUploadedModelSelectionParams } from '@/pages/ipe/workbench/utils/tryOnSystemModelPresetFallback';

export function validateGoodsStudioSubmitParams(params = {}) {
  const normalized = normalizeGoodsStudioParams(params);
  if (normalized.modelMode === 'system' && !String(normalized.selectedPresetId || normalized.selectedPresetUrl || '').trim()) {
    return { ok: false, message: i18n.t('workbench:ipeUtilError.selectLocalModel') };
  }
  if (normalized.modelMode === 'custom' && !String(normalized.modelImageUrl || '').trim()) {
    return { ok: false, message: i18n.t('workbench:ipeUtilError.uploadCustomModel') };
  }
  return { ok: true, params: normalized };
}

export async function resolveGoodsStudioSubmitParams(params = {}) {
  const validation = validateGoodsStudioSubmitParams(params);
  if (!validation.ok) {
    throw new Error(validation.message);
  }

  const normalized = { ...validation.params };
  let modelImageUrl = '';

  if (normalized.modelMode === 'custom') {
    modelImageUrl = await resolveImg2ImgReferenceForApi(normalized.modelImageUrl);
    if (!modelImageUrl) {
      throw new Error(i18n.t('workbench:ipeUtilError.customModelUnresolvedUpload'));
    }
  } else if (normalized.modelMode === 'system') {
    const presetUrl = resolveModelEthnicityPresetPersistedUrl({
      id: normalized.selectedPresetId,
      displayUrl: normalized.selectedPresetUrl,
      url: normalized.selectedPresetUrl,
    });
    modelImageUrl = await resolveImg2ImgReferenceForApi(presetUrl);
    if (!modelImageUrl) {
      throw new Error(i18n.t('workbench:ipeUtilError.localModelUnresolvedReselect'));
    }
  }

  const submitParams = {
    ...GOODS_STUDIO_DEFAULT_PARAMS,
    ...normalized,
    modelEthnicity: normalized.modelEthnicity || DEFAULT_MODEL_TRY_ON_ETHNICITY,
    modelImageUrl: modelImageUrl || '',
  };

  if (normalized.modelMode === 'custom' && normalized.usedUploadedModel) {
    Object.assign(submitParams, buildTryOnUploadedModelSelectionParams({
      modelImageUrl: normalized.modelImageUrl,
      modelImagePreviewUrl: normalized.modelImagePreviewUrl,
    }));
  }

  if (normalized.modelMode === 'system' && normalized.selectedPresetId) {
    Object.assign(submitParams, buildModelEthnicitySystemModelSelectionParams({
      id: normalized.selectedPresetId,
      apiId: normalized.selectedPresetId,
      displayUrl: normalized.selectedPresetUrl,
      url: normalized.selectedPresetUrl,
    }));
  }

  if (normalized.modelMode === 'ai' || normalized.modelMode === 'none') {
    submitParams.modelImageUrl = '';
    submitParams.modelImagePreviewUrl = '';
    submitParams.usedUploadedModel = false;
    submitParams.selectedPresetId = '';
    submitParams.selectedPresetUrl = '';
  }

  return submitParams;
}
